import { useState } from "react";
import { ArrowLeftRight, ShoppingCart, Star, Truck, ShieldCheck, Search, User, Menu, Heart } from "lucide-react";

/**
 * Before/After Shopify storefront comparison.
 * Hand-crafted (no external images) so it always looks pixel-clean.
 * Drag the divider to compare a "stock template" store vs a Kavion-rebuilt store.
 */
export default function BeforeAfter() {
  const [pos, setPos] = useState(50);

  return (
    <section data-testid="before-after-section" className="relative py-20 sm:py-24 md:py-32 bg-[#070709] border-y border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 sm:mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-blue-400 font-bold">Redesign Showcase</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
            From outdated → <span className="text-gradient">conversion machine.</span>
          </h2>
          <p className="mt-4 text-zinc-400 text-base md:text-lg">
            Drag the slider to see how a CRO-first redesign reshapes the buyer journey — and the speed score with it.
          </p>
        </div>

        <div
          className="relative w-full overflow-hidden rounded-2xl sm:rounded-3xl border border-white/10 select-none"
          style={{ aspectRatio: "16/10" }}
        >
          {/* AFTER (full layer) — clean Kavion redesign */}
          <div className="absolute inset-0">
            <AfterStore />
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-2 z-20">
              <div className="hidden sm:flex px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-bold uppercase tracking-wider backdrop-blur-md">
                After • +312% CVR
              </div>
              <SpeedBadge value={99} tone="green" testid="speed-score-after" />
            </div>
          </div>

          {/* BEFORE clipped layer — ugly stock template */}
          <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
            <BeforeStore />
            <div className="absolute top-3 left-3 sm:top-4 sm:left-4 flex items-center gap-2 z-20">
              <div className="hidden sm:flex px-3 py-1.5 rounded-full bg-zinc-900/80 text-zinc-100 text-xs font-bold uppercase tracking-wider backdrop-blur-md">
                Before
              </div>
              <SpeedBadge value={43} tone="red" testid="speed-score-before" />
            </div>
          </div>

          {/* Handle */}
          <div
            className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_20px_rgba(96,165,250,0.6)] cursor-ew-resize z-30 pointer-events-none"
            style={{ left: `${pos}%` }}
          >
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white grid place-items-center text-black shadow-2xl">
              <ArrowLeftRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
          </div>

          <input
            data-testid="before-after-slider"
            type="range"
            min="0"
            max="100"
            value={pos}
            onChange={(e) => setPos(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-40"
            aria-label="Compare before and after"
          />
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
          <span className="inline-flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-red-400" /> Before redesign
          </span>
          <span className="inline-flex items-center gap-2">
            After redesign <span className="h-2 w-2 rounded-full bg-emerald-400" />
          </span>
        </div>
      </div>
    </section>
  );
}

/* ---------------- helpers ---------------- */

function SpeedBadge({ value, tone, testid }) {
  const styles = tone === "green"
    ? { wrap: "bg-emerald-500/15 border-emerald-400/40 text-emerald-300", ring: "border-emerald-400/60" }
    : { wrap: "bg-red-500/15 border-red-400/40 text-red-300", ring: "border-red-400/60" };
  return (
    <div
      data-testid={testid}
      className={`flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full border ${styles.wrap} text-[10px] sm:text-xs font-bold backdrop-blur-md`}
    >
      <span className={`relative grid place-items-center h-5 w-5 sm:h-6 sm:w-6 rounded-full border-2 ${styles.ring} text-[9px] sm:text-[10px]`}>
        {value}
      </span>
      <span className="uppercase tracking-wider">Speed</span>
    </div>
  );
}

/* The "after" store — clean, premium Kavion-style Shopify hero */
function AfterStore() {
  return (
    <div className="h-full w-full bg-gradient-to-br from-[#0a0a0e] via-[#0e0e16] to-[#08080c] relative">
      {/* nav */}
      <div className="flex items-center gap-4 px-4 sm:px-10 py-4 border-b border-white/5">
        <div className="font-display font-extrabold text-white text-sm sm:text-base tracking-tight">AURORA<span className="text-blue-400">.</span></div>
        <div className="hidden md:flex items-center gap-5 text-[11px] text-zinc-300 ml-4">
          <span>Shop</span><span>New</span><span>Bundles</span><span>Journal</span>
        </div>
        <div className="ml-auto flex items-center gap-3 text-zinc-300">
          <Search className="h-3.5 w-3.5" />
          <User className="h-3.5 w-3.5" />
          <ShoppingCart className="h-3.5 w-3.5" />
        </div>
      </div>
      {/* hero */}
      <div className="grid md:grid-cols-2 gap-4 sm:gap-8 items-center px-4 sm:px-10 py-5 sm:py-8">
        <div className="space-y-2 sm:space-y-3">
          <div className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-blue-300 bg-blue-500/10 border border-blue-400/20 rounded-full px-2 py-0.5">
            <Star className="h-2.5 w-2.5 fill-blue-300" /> 4.9 · 12,840 reviews
          </div>
          <div className="font-display font-extrabold text-white leading-[0.95] tracking-tight text-2xl sm:text-3xl md:text-4xl">
            Studio-grade<br />sound. Reimagined.
          </div>
          <div className="text-[10px] sm:text-xs text-zinc-400 leading-snug max-w-[24ch]">
            Spatial audio with adaptive noise control. Engineered in Tokyo.
          </div>
          <div className="flex items-center gap-2 pt-1 sm:pt-2">
            <div className="bg-blue-600 text-white text-[10px] sm:text-xs font-semibold rounded-full px-3 py-1.5 sm:px-4 sm:py-2 inline-flex items-center gap-1">
              Add to Cart <span className="opacity-60">· $349</span>
            </div>
            <div className="bg-white/5 border border-white/10 text-white text-[10px] sm:text-xs font-semibold rounded-full px-3 py-1.5 sm:px-4 sm:py-2">
              Watch film
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2 text-[8.5px] sm:text-[10px] text-zinc-500">
            <span className="inline-flex items-center gap-1"><Truck className="h-2.5 w-2.5" /> Free 2-day</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-2.5 w-2.5" /> 30-day return</span>
          </div>
        </div>
        {/* product visual */}
        <div className="relative aspect-square rounded-xl sm:rounded-2xl bg-gradient-to-br from-indigo-700/40 via-blue-600/25 to-transparent border border-white/10 grid place-items-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(96,165,250,0.35),transparent_55%)]" />
          <div className="relative">
            <div className="h-20 w-20 sm:h-28 sm:w-28 rounded-full bg-gradient-to-br from-blue-300 via-blue-500 to-indigo-700 glow-blue" />
            <div className="absolute -inset-2 rounded-full ring-1 ring-white/20" />
          </div>
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[9px] text-white/80">
            <span className="bg-black/60 backdrop-blur rounded-full px-2 py-0.5">Onyx</span>
            <span className="bg-black/60 backdrop-blur rounded-full px-2 py-0.5">In stock</span>
          </div>
        </div>
      </div>
      {/* sub-row */}
      <div className="hidden sm:flex items-center justify-between gap-2 px-10 pb-6">
        {["Apple Pay", "Klarna", "Shop Pay", "Visa", "Mastercard"].map((t) => (
          <span key={t} className="text-[10px] text-zinc-500 tracking-wider uppercase">{t}</span>
        ))}
      </div>
    </div>
  );
}

/* The "before" store — cluttered stock-template Shopify */
function BeforeStore() {
  return (
    <div className="h-full w-full bg-gradient-to-b from-zinc-100 to-zinc-200 text-zinc-700 relative">
      {/* announcement bar */}
      <div className="bg-yellow-300 text-zinc-900 text-[9px] sm:text-[10px] text-center py-1 font-bold tracking-wide">
        ★ MEGA SUMMER SALE!! Use code SAVE20 — Limited Time!! ★
      </div>
      {/* nav */}
      <div className="flex items-center gap-3 px-3 sm:px-6 py-2 sm:py-3 bg-white border-b border-zinc-300">
        <Menu className="h-3.5 w-3.5 text-zinc-700 md:hidden" />
        <div className="font-bold text-zinc-900 text-xs sm:text-sm" style={{ fontFamily: "Times New Roman, serif" }}>
          Aurora Audio Co.
        </div>
        <div className="hidden md:flex items-center gap-3 text-[10px] text-zinc-700 ml-2">
          <span>HOME</span><span>SHOP</span><span>BLOG</span><span>ABOUT</span><span>CONTACT US</span><span>FAQ</span>
        </div>
        <div className="ml-auto flex items-center gap-3 text-zinc-600">
          <Search className="h-3 w-3" />
          <Heart className="h-3 w-3" />
          <ShoppingCart className="h-3 w-3" />
        </div>
      </div>
      {/* hero - cramped */}
      <div className="px-3 sm:px-6 py-4 sm:py-5 grid grid-cols-3 gap-2 sm:gap-3">
        <div className="col-span-2 space-y-1.5">
          <div className="bg-red-600 text-white text-[8px] sm:text-[9px] inline-block px-1.5 py-0.5 font-bold">SALE 20% OFF</div>
          <div className="font-bold text-zinc-900 text-base sm:text-xl leading-tight" style={{ fontFamily: "Arial, sans-serif" }}>
            Welcome to Our Online Store
          </div>
          <div className="text-[9px] sm:text-[11px] text-zinc-700 leading-snug">
            We sell the best wireless headphones at very best prices. Shop now and get free shipping on orders over $100. Subscribe to our newsletter!
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <div className="bg-zinc-700 text-white text-[9px] px-2 py-1 rounded">SHOP NOW</div>
            <div className="bg-orange-500 text-white text-[9px] px-2 py-1 rounded">CLICK HERE!!</div>
            <div className="bg-zinc-300 text-zinc-800 text-[9px] px-2 py-1 rounded">Learn More</div>
          </div>
          {/* fake reviews + clutter */}
          <div className="flex items-center gap-1 text-[8px] text-yellow-600">
            <Star className="h-2.5 w-2.5 fill-yellow-500" />
            <Star className="h-2.5 w-2.5 fill-yellow-500" />
            <Star className="h-2.5 w-2.5 fill-yellow-500" />
            <Star className="h-2.5 w-2.5 fill-yellow-500" />
            <Star className="h-2.5 w-2.5" />
            <span className="text-zinc-600 ml-1">(based on 12 reviews)</span>
          </div>
          <div className="bg-zinc-200 border border-dashed border-zinc-400 p-1.5 rounded text-[8.5px] text-zinc-700 max-w-[28ch]">
            📧 Sign up to our newsletter to receive updates and 10% off!
          </div>
        </div>
        <div className="col-span-1 grid grid-rows-2 gap-1.5">
          <div className="bg-zinc-300 grid place-items-center rounded">
            <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-zinc-500" />
          </div>
          <div className="bg-zinc-400 grid place-items-center rounded text-[8px] text-white font-bold">
            BANNER AD
          </div>
        </div>
      </div>
      {/* product grid below */}
      <div className="px-3 sm:px-6 pb-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`bg-white border border-zinc-300 p-1.5 ${i === 4 ? "hidden sm:block" : ""}`}>
            <div className="aspect-square bg-zinc-200 rounded-sm mb-1" />
            <div className="text-[8px] text-zinc-700 truncate">Headphone Model {i}</div>
            <div className="text-[9px] font-bold text-red-700">$299.99 <span className="text-zinc-400 line-through font-normal">$399</span></div>
          </div>
        ))}
      </div>
      {/* footer ribbon */}
      <div className="absolute bottom-0 inset-x-0 bg-zinc-800 text-white text-[8px] sm:text-[9px] px-3 py-1 flex items-center gap-3">
        <span>📞 1-800-SHOP-NOW</span>
        <span className="hidden sm:inline">✉ contact@aurora.com</span>
        <span className="ml-auto">© 2018 Aurora Audio Co.</span>
      </div>
    </div>
  );
}
